// Admin app entry point. Connects to the game server's WS, runs an admin
// login + tabbed catalogue editors (items, mobs, affixes, quests, npcs,
// spells). Vanilla TS + DOM, same patterns as /client.

import type {
  AffixDefinition,
  GameConfig,
  ItemTemplate,
  MobTemplate,
  NpcTemplate,
  QuestTemplate,
  SpellTemplate,
  ZoneJson,
} from "@vinyl/shared";
import { el, mount } from "./dom.js";
import { createAffixesScreen } from "./affixes-screen.js";
import { createConfigScreen } from "./config-screen.js";
import { createItemsScreen } from "./items-screen.js";
import { createLoginScreen, humanizeAdminLoginError } from "./login.js";
import { createMobsScreen } from "./mobs-screen.js";
import { createNpcsScreen } from "./npcs-screen.js";
import { createQuestsScreen } from "./quests-screen.js";
import { createSpellsScreen } from "./spells-screen.js";
import { createZonesScreen } from "./zones-screen.js";
import { createAccessScreen } from "./access-screen.js";
import { createUsersScreen } from "./users-screen.js";
import { AdminClient } from "./net.js";
import "./styles.css";

type TabId =
  | "items"
  | "mobs"
  | "affixes"
  | "quests"
  | "npcs"
  | "spells"
  | "gameConfig"
  | "zones"
  | "access"
  | "users";
const ALL_TABS: TabId[] = [
  "items",
  "mobs",
  "affixes",
  "quests",
  "npcs",
  "spells",
  "gameConfig",
  "zones",
  "access",
  "users",
];

// M12 — tabs without a draft/publish flow (every action commits immediately).
// They're skipped by the dirty/published topbar logic and the publishCurrent
// switch. Use this set when adding more "live action" tabs in the future.
const NO_PUBLISH_TABS = new Set<TabId>(["access", "users"]);

// Dev: Vite serves the page on :5174, backend is on :3001 (plain ws).
// Prod: page served via https by Caddy, which proxies /ws → :3001 (wss).
const SERVER_URL =
  location.port === "5174"
    ? `ws://${location.hostname}:3001`
    : `${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}/ws`;

const root = document.getElementById("app");
if (!root) throw new Error("missing #app");

const client = new AdminClient(SERVER_URL);

// Toast --------------------------------------------------------------------
const toast = el("div", { class: "toast" });
document.body.append(toast);
let toastTimer = 0;

function showToast(text: string, kind: "info" | "error" | "success" = "info"): void {
  toast.textContent = text;
  toast.className = `toast visible${kind === "error" ? " error" : kind === "success" ? " success" : ""}`;
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toast.classList.remove("visible");
  }, 2800);
}

// Catalogue cross-ref caches — fed from every successful catalogueUpdate so
// downstream form dropdowns (mob loot → item, quest objective → mob, NPC
// vendor/quests → items+quests, zone spawn → mob+npc) only ever surface
// real ids.
let knownItemIds: string[] = [];
let knownMobIds: string[] = [];
let knownQuestIds: string[] = [];
let knownNpcIds: string[] = [];

const dirty: Record<TabId, boolean> = {
  items: false,
  mobs: false,
  affixes: false,
  quests: false,
  npcs: false,
  spells: false,
  gameConfig: false,
  zones: false,
  access: false,
  users: false,
};
const lastPublishedAt: Record<TabId, number | null> = {
  items: null,
  mobs: null,
  affixes: null,
  quests: null,
  npcs: null,
  spells: null,
  gameConfig: null,
  zones: null,
  access: null,
  users: null,
};
// M12 — pending access-requests count; powers the sidebar badge so the
// admin sees new demands at a glance even when on another tab.
let accessPendingCount = 0;
let currentTab: TabId = "items";

const itemsScreen = createItemsScreen({
  onDirtyChange: (d) => {
    dirty.items = d;
    if (currentTab === "items") refreshTopbar();
  },
});
const mobsScreen = createMobsScreen({
  getKnownItemIds: () => knownItemIds,
  onDirtyChange: (d) => {
    dirty.mobs = d;
    if (currentTab === "mobs") refreshTopbar();
  },
});
const affixesScreen = createAffixesScreen({
  onDirtyChange: (d) => {
    dirty.affixes = d;
    if (currentTab === "affixes") refreshTopbar();
  },
});
const questsScreen = createQuestsScreen({
  getKnownMobIds: () => knownMobIds,
  getKnownItemIds: () => knownItemIds,
  onDirtyChange: (d) => {
    dirty.quests = d;
    if (currentTab === "quests") refreshTopbar();
  },
});
const npcsScreen = createNpcsScreen({
  getKnownItemIds: () => knownItemIds,
  getKnownQuestIds: () => knownQuestIds,
  onDirtyChange: (d) => {
    dirty.npcs = d;
    if (currentTab === "npcs") refreshTopbar();
  },
});
const spellsScreen = createSpellsScreen({
  onDirtyChange: (d) => {
    dirty.spells = d;
    if (currentTab === "spells") refreshTopbar();
  },
});
const configScreen = createConfigScreen({
  onDirtyChange: (d) => {
    dirty.gameConfig = d;
    if (currentTab === "gameConfig") refreshTopbar();
  },
});
const zonesScreen = createZonesScreen({
  getKnownMobIds: () => knownMobIds,
  getKnownNpcIds: () => knownNpcIds,
  onDirtyChange: (d) => {
    dirty.zones = d;
    if (currentTab === "zones") refreshTopbar();
  },
});
const accessScreen = createAccessScreen({
  onAdd: (email) => client.send({ t: "adminAddAccessEmail", d: { email } }),
  onApprove: (email) => client.send({ t: "adminApproveAccessRequest", d: { email } }),
  onReject: (email) => client.send({ t: "adminRejectAccessRequest", d: { email } }),
  onBulkRejectByIp: (ip) => client.send({ t: "adminBulkRejectByIp", d: { ip } }),
  onRefresh: (filter) => client.send({ t: "adminListAccessRequests", d: filter }),
});
const usersScreen = createUsersScreen({
  onInvalidate: (accountId) =>
    client.send({ t: "adminInvalidateUserSessions", d: { accountId } }),
  onRefresh: () => client.send({ t: "adminListUsers", d: {} }),
});

const loginScreen = createLoginScreen({
  onSubmit: (password) => {
    loginScreen.setSubmitting(true);
    loginScreen.setError(null);
    client.send({ t: "adminLogin", d: { password } });
  },
});

// Sidebar nav (left) + topbar (right) -------------------------------------
const TAB_META: Record<TabId, { icon: string; label: string }> = {
  items: { icon: "⚔", label: "Items" },
  mobs: { icon: "🐺", label: "Mobs" },
  affixes: { icon: "✦", label: "Affixes" },
  quests: { icon: "📜", label: "Quests" },
  npcs: { icon: "👤", label: "NPCs" },
  spells: { icon: "✨", label: "Spells" },
  gameConfig: { icon: "⚙", label: "Config" },
  zones: { icon: "🗺", label: "Zones" },
  access: { icon: "🔑", label: "Access" },
  users: { icon: "👥", label: "Users" },
};

const tabButtons: Record<TabId, HTMLElement> = {} as Record<TabId, HTMLElement>;
const sidebarNav = el("nav", { class: "sidebar-nav" });
for (const t of ALL_TABS) {
  const meta = TAB_META[t];
  const btn = el(
    "button",
    { class: "sidebar-tab", onclick: () => switchTab(t) },
    el("span", { class: "sidebar-tab-icon" }, meta.icon),
    el("span", { class: "sidebar-tab-label" }, meta.label),
  );
  tabButtons[t] = btn;
  sidebarNav.append(btn);
}

const rebootBtn = el(
  "button",
  {
    class: "button secondary sidebar-reboot",
    title: "Save state and restart the server (tsx watch picks it up). Useful after publishing zones so the world re-instantiates from the new catalogue.",
    onclick: () => {
      const anyDirty = ALL_TABS.some((t) => dirty[t]);
      if (anyDirty && !confirm("You have unpublished changes — they will be lost on reboot. Continue?")) return;
      if (!confirm("Reboot the server now? All in-game players will be disconnected and need to reconnect.")) return;
      client.send({ t: "adminRebootServer", d: {} });
      showToast("Rebooting server… page will reload in 3 s.", "info");
      // The WS will close cleanly when the server exits; the disconnect
      // banner appears too. Auto-reload after the server has had time
      // to restart (tsx watch typically takes ~1-2 s on this codebase).
      window.setTimeout(() => window.location.reload(), 3000);
    },
  },
  "↻ Reboot server",
);

const logoutBtn = el(
  "button",
  {
    class: "button secondary sidebar-logout",
    onclick: () => {
      const anyDirty = ALL_TABS.some((t) => dirty[t]);
      if (anyDirty && !confirm("Unpublished changes will be lost. Log out?")) return;
      window.location.reload();
    },
  },
  "Log out",
);

const sidebar = el(
  "aside",
  { class: "sidebar" },
  el(
    "div",
    { class: "sidebar-brand" },
    el("div", { class: "sidebar-brand-name" }, "Vinyl"),
    el("div", { class: "sidebar-brand-sub" }, "Admin console"),
  ),
  sidebarNav,
  rebootBtn,
  logoutBtn,
);

const topbarTitle = el("h1", { class: "topbar-title" }, "");
const statusEl = el("span", { class: "status" }, "");
const publishBtn = el(
  "button",
  { class: "button publish", onclick: () => publishCurrent() },
  "Publish",
);
const topbar = el(
  "header",
  { class: "topbar" },
  topbarTitle,
  el("div", { class: "topbar-actions" }, statusEl, publishBtn),
);

const contentArea = el("div", { class: "content-area" });
const appShell = el(
  "div",
  { class: "shell" },
  sidebar,
  el("div", { class: "shell-main" }, topbar, contentArea),
);

function rootForTab(t: TabId): HTMLElement {
  switch (t) {
    case "items":
      return itemsScreen.root;
    case "mobs":
      return mobsScreen.root;
    case "affixes":
      return affixesScreen.root;
    case "quests":
      return questsScreen.root;
    case "npcs":
      return npcsScreen.root;
    case "spells":
      return spellsScreen.root;
    case "gameConfig":
      return configScreen.root;
    case "zones":
      return zonesScreen.root;
    case "access":
      return accessScreen.root;
    case "users":
      return usersScreen.root;
  }
}

function switchTab(tab: TabId): void {
  currentTab = tab;
  for (const t of ALL_TABS) {
    tabButtons[t].classList.toggle("active", t === tab);
  }
  topbarTitle.textContent = TAB_META[tab].label;
  mount(contentArea, rootForTab(tab));
  refreshTopbar();
  // M12/M13 — live-action tabs use their own list endpoints; catalogue fetch
  // doesn't apply (would be rejected by the server as an unknown catalogue name).
  if (tab === "access") {
    client.send({ t: "adminListAccessRequests", d: {} });
  } else if (tab === "users") {
    client.send({ t: "adminListUsers", d: {} });
  } else {
    client.send({ t: "adminFetchCatalogue", d: { catalogue: tab } });
  }
}

function refreshAccessBadge(): void {
  const btn = tabButtons.access;
  if (!btn) return;
  const labelEl = btn.querySelector(".sidebar-tab-label");
  if (!labelEl) return;
  labelEl.textContent =
    accessPendingCount > 0
      ? `${TAB_META.access.label} (${accessPendingCount})`
      : TAB_META.access.label;
  btn.classList.toggle("has-pending", accessPendingCount > 0);
}

function refreshTopbar(): void {
  // M12 — live-action tabs (access) have no dirty/publish state. Hide
  // both the status text and the publish button outright.
  if (NO_PUBLISH_TABS.has(currentTab)) {
    statusEl.className = "status";
    statusEl.textContent = "";
    (publishBtn as HTMLButtonElement).style.display = "none";
    return;
  }
  (publishBtn as HTMLButtonElement).style.display = "";
  const isDirty = dirty[currentTab];
  const published = lastPublishedAt[currentTab];
  if (isDirty) {
    statusEl.className = "status dirty";
    statusEl.textContent = "Unpublished changes";
    (publishBtn as HTMLButtonElement).disabled = false;
  } else if (published) {
    statusEl.className = "status published";
    const secs = Math.round((Date.now() - published) / 1000);
    statusEl.textContent =
      secs < 60 ? "Published just now" : `Published ${Math.floor(secs / 60)}m ago`;
    (publishBtn as HTMLButtonElement).disabled = true;
  } else {
    statusEl.className = "status";
    statusEl.textContent = "No changes";
    (publishBtn as HTMLButtonElement).disabled = true;
  }
}

function publishCurrent(): void {
  switch (currentTab) {
    case "items": {
      const items: ItemTemplate[] = itemsScreen.getDraft();
      if (items.length === 0) return void showToast("Catalogue cannot be empty.", "error");
      client.send({ t: "adminPublishItems", d: { items } });
      return;
    }
    case "mobs": {
      const mobs: MobTemplate[] = mobsScreen.getDraft();
      if (mobs.length === 0) return void showToast("Catalogue cannot be empty.", "error");
      client.send({ t: "adminPublishMobs", d: { mobs } });
      return;
    }
    case "affixes": {
      const affixes: AffixDefinition[] = affixesScreen.getDraft();
      if (affixes.length === 0) return void showToast("Pool cannot be empty.", "error");
      client.send({ t: "adminPublishAffixes", d: { affixes } });
      return;
    }
    case "quests": {
      const quests: QuestTemplate[] = questsScreen.getDraft();
      if (quests.length === 0) return void showToast("Catalogue cannot be empty.", "error");
      client.send({ t: "adminPublishQuests", d: { quests } });
      return;
    }
    case "npcs": {
      const npcs: NpcTemplate[] = npcsScreen.getDraft();
      if (npcs.length === 0) return void showToast("Catalogue cannot be empty.", "error");
      client.send({ t: "adminPublishNpcs", d: { npcs } });
      return;
    }
    case "spells": {
      const spells: SpellTemplate[] = spellsScreen.getDraft();
      if (spells.length === 0) return void showToast("Catalogue cannot be empty.", "error");
      client.send({ t: "adminPublishSpells", d: { spells } });
      return;
    }
    case "gameConfig": {
      const config: GameConfig = configScreen.getDraft();
      client.send({ t: "adminPublishGameConfig", d: { config } });
      return;
    }
    case "zones": {
      const zones: ZoneJson[] = zonesScreen.getDraft();
      if (zones.length === 0) return void showToast("Catalogue cannot be empty.", "error");
      client.send({ t: "adminPublishZones", d: { zones } });
      return;
    }
    case "access":
    case "users":
      // M12/M13 — live-action tabs, no publish flow. Button is hidden via
      // NO_PUBLISH_TABS so this case is unreachable in normal use; kept
      // here only to satisfy the exhaustive switch.
      return;
  }
}

function showLogin(): void {
  mount(root!, loginScreen.root);
}

function showApp(): void {
  mount(root!, appShell);
  switchTab("items");
  // M12 — prime the pending-count badge so it's visible from any tab
  // immediately after login, without waiting for the user to open Access.
  client.send({ t: "adminListAccessRequests", d: {} });
}

// Sticky banner shown while the WS is down. We don't auto-reconnect (the
// admin app is intentionally simple — see admin/src/net.ts), but the
// transient toast is too easy to miss, so a visible bar persists until
// the user reloads.
const disconnectBanner = el(
  "div",
  { class: "disconnect-banner" },
  el("span", {}, "⚠ Server connection lost — admin actions are not being sent."),
  el(
    "button",
    {
      class: "button danger disconnect-banner-reload",
      onclick: () => window.location.reload(),
    },
    "Reload now",
  ),
);
disconnectBanner.style.display = "none";
document.body.append(disconnectBanner);

client.onConnection((open) => {
  disconnectBanner.style.display = open ? "none" : "flex";
  if (!open) {
    showToast("Server connection lost. Reload to retry.", "error");
  }
});

client.onMessage((msg) => {
  switch (msg.t) {
    case "adminLoginOk":
      showApp();
      return;
    case "adminLoginError":
      loginScreen.setSubmitting(false);
      loginScreen.setError(humanizeAdminLoginError(msg.d.reason));
      return;
    case "catalogueUpdate": {
      switch (msg.d.catalogue) {
        case "items":
          itemsScreen.setItems(msg.d.items);
          knownItemIds = msg.d.items.map((i) => i.id);
          break;
        case "mobs":
          mobsScreen.setMobs(msg.d.mobs);
          knownMobIds = msg.d.mobs.map((m) => m.id);
          break;
        case "affixes":
          affixesScreen.setAffixes(msg.d.affixes);
          break;
        case "quests":
          questsScreen.setQuests(msg.d.quests);
          knownQuestIds = msg.d.quests.map((q) => q.id);
          break;
        case "npcs":
          npcsScreen.setNpcs(msg.d.npcs);
          knownNpcIds = msg.d.npcs.map((n) => n.id);
          break;
        case "spells":
          spellsScreen.setSpells(msg.d.spells);
          break;
        case "gameConfig":
          configScreen.setConfig(msg.d.config);
          break;
        case "zones":
          zonesScreen.setZones(msg.d.zones);
          break;
      }
      return;
    }
    case "adminPublishResult": {
      const cat = msg.d.catalogue as TabId;
      if (msg.d.ok) {
        lastPublishedAt[cat] = Date.now();
        switch (cat) {
          case "items":
            itemsScreen.markPublished();
            break;
          case "mobs":
            mobsScreen.markPublished();
            break;
          case "affixes":
            affixesScreen.markPublished();
            break;
          case "quests":
            questsScreen.markPublished();
            break;
          case "npcs":
            npcsScreen.markPublished();
            break;
          case "spells":
            spellsScreen.markPublished();
            break;
          case "gameConfig":
            configScreen.markPublished();
            break;
          case "zones":
            zonesScreen.markPublished();
            break;
        }
        showToast(`Published ${msg.d.count} ${cat}`, "success");
      } else {
        showToast(`Publish failed: ${msg.d.error}`, "error");
      }
      if (cat === currentTab) refreshTopbar();
      return;
    }
    case "rateLimited": {
      const secs = Math.max(1, Math.ceil(msg.d.retryAfterMs / 1000));
      showToast(`Rate limited — retry in ${secs}s`, "error");
      loginScreen.setSubmitting(false);
      return;
    }
    case "accessRequestList": {
      accessPendingCount = msg.d.pendingCount;
      refreshAccessBadge();
      accessScreen.setRows(msg.d.rows);
      return;
    }
    case "accessRequestError": {
      accessScreen.setError(msg.d.reason);
      return;
    }
    case "userList": {
      usersScreen.setRows(msg.d.rows);
      return;
    }
    case "userActionError": {
      usersScreen.setError(msg.d.reason);
      return;
    }
    default:
      return;
  }
});

client.connect();
showLogin();
window.setInterval(refreshTopbar, 30_000);
